import DomainHandler from "./domainHandler";

export interface WhiteLabelVariablesInterface {
  [key: string]: any;
  apiDomain?: string;
}

interface ComponentRegistry {
  [key: string]: () => Promise<any>;
}

class WhiteLabelRegistry {
  private registry: ComponentRegistry = {};
  private loadedComponents = new Map<string, any>();
  private failedComponents = new Set<string>();
  private loadingPromises = new Map<string, Promise<any>>();
  private variables: Record<string, any> = {};
  private variablesLoaded = false;
  private variablesLoadAttempted = false;

  constructor() {
    // Auto-discover all white label components
    const modules = import.meta.glob("./tenants/**/*.vue", { eager: false });

    Object.entries(modules).forEach(([path, loader]) => {
      const normalized = path.replace(/^\.\.\/whitelabel\//, "");
      this.registry[normalized] = loader;
    });
    // Variables will be loaded via initVariables()
  }

  async initVariables(): Promise<void> {
    if (this.variablesLoaded) return;

    this.variablesLoadAttempted = true;
    const customer = DomainHandler.getHostname();

    if (customer !== "default") {
      const variablesPath = `./tenants/${customer}/WhiteLabelVariables.ts`;
      const variablesJSON = import.meta.glob("./tenants/**/WhiteLabelVariables.ts");
      const importFn = variablesJSON[variablesPath];

      if (importFn) {
        try {
          const module = await importFn();
          this.variables = (module as { default: any }).default;
        } catch (error) {
          this.variables = {};
        }
      } else {
        this.variables = {};
      }
    } else {
      this.variables = {};
    }
    this.variablesLoaded = true;
  }

  async loadComponent(componentName: string): Promise<any | null> {
    // Get customer name dynamically each time to support testing
    const customer = DomainHandler.getHostname();
    const componentPath = `./tenants/${customer}/${componentName}.vue`;

    // Return null immediately if we've already tried and failed
    if (this.failedComponents.has(componentPath)) {
      return null;
    }

    // Return cached component if already loaded
    const cachedComponent = this.loadedComponents.get(componentPath);
    if (cachedComponent) {
      return cachedComponent;
    }

    // Check if we're already loading this component to avoid duplicate requests
    const existingPromise = this.loadingPromises.get(componentPath);
    if (existingPromise) {
      return existingPromise;
    }

    // Start loading the component
    const loadingPromise = this.loadComponentInternal(componentPath);
    this.loadingPromises.set(componentPath, loadingPromise);

    try {
      const component = await loadingPromise;
      this.loadingPromises.delete(componentPath);
      return component;
    } catch (error) {
      this.loadingPromises.delete(componentPath);
      return null;
    }
  }

  getVariables(): Record<string, any> {
    return this.variables;
  }

  getVariable(key: string): any {
    if (!this.variablesLoaded && !this.variablesLoadAttempted) {
      console.warn(
        `White-label variables not initialized when requesting '${key}'. ` +
          `Ensure initVariables() is called in main.js before component initialization.`
      );
      this.initVariables(); // Attempt to load variables if not already done
    }
    return this.variables[key];
  }

  private async loadComponentInternal(componentPath: string): Promise<any | null> {
    // Load the component
    const loader = this.registry[componentPath];
    if (!loader) {
      this.failedComponents.add(componentPath);
      return null;
    }

    try {
      const module = await loader();
      const component = module.default;
      this.loadedComponents.set(componentPath, component);
      return component;
    } catch (error) {
      this.failedComponents.add(componentPath);
      throw error;
    }
  }
}

export const whiteLabelRegistry = new WhiteLabelRegistry();
export { WhiteLabelRegistry };
